import { useEffect, useRef, useState } from "react";
import { Bar, BarChart, Tooltip } from "recharts";
import { useSelector } from "react-redux";
import _ from "lodash";

import CustomTooltip from "./tooltip";

import styles from "./pageAnalytics.module.scss";

interface PageAnalyticsProps {
  analytics: {
    views: number;
    downloads: number;
    avgTimeSpent: number;
    shares: number;
    type: string;
    pageCount: number;
    dataPoints: {
      totalTimeSpent: number;
      count: number;
      avgTimeSpent: number;
      index: number;
    }[];
  };
}

const MAX_PAGE_COUNT = 6;

const PageAnalytics = ({ analytics }: PageAnalyticsProps) => {
  const barChartRef = useRef<any>();

  const [chart, setChart] = useState({
    width: 300,
    visible: false,
  });

  const [pages, setPages] = useState<any>([]);

  const preview = useSelector((state: any) =>
    _.get(state, "asset.preview", null),
  );

  const pagesData = _.get(preview, "item.pages", []);

  const shouldRenderChart = analytics.pageCount > MAX_PAGE_COUNT;

  let totalPages = analytics.pageCount;
  let width = totalPages * 13;

  if (width > chart.width) {
    width = chart.width;
  }

  let height = 150;
  // TODO - > NEED TO ADD PRIMARY COLOR AND SEPARATOR COLOR VARIABLES
  const color = "#000000";
  const primaryColor = "#E51058";
  const separatorColor = "#DBDBDB";

  const calculatePageAnalytics = () => {
    if (
      !analytics ||
      analytics.pageCount == 0 ||
      !analytics.dataPoints ||
      analytics.dataPoints.length == 0
    ) {
      return [];
    }

    var pagesArr: { index: number; avgTimeSpent: number }[] = [];
    for (var i = 1; i <= analytics.pageCount; i++) {
      let page = {
        index: i,
        avgTimeSpent: 0.0,
      };

      pagesArr.push(page);
    }

    analytics.dataPoints.map((element) => {
      pagesArr[element.index - 1] = element;
    });

    return pagesArr;
  };

  const renderTooltip = (props: any) => {
    var { payload } = props;

    if (payload && payload.length > 0) {
      var index = _.get(payload, "[0].payload.index", 0);
      var data = pages[index - 1];
      if (barChartRef.current) {
        data = barChartRef.current.props.data[index - 1];
      }
      return (
        <CustomTooltip
          iconDimensions={{ width: 180, height: 85 }}
          timeSpentLabel="Avg. time spent:"
          activePage={data}
          preview={preview}
        />
      );
    }

    return false;
  };

  useEffect(() => {
    setPages(calculatePageAnalytics());
  }, [analytics]);

  return (
    <div id="page_analytics_chart">
      {!shouldRenderChart && (
        <div className={styles.pageAnalytics}>
          {pagesData.map((page: any) => {
            return (
              <PageData page={page} key={page.index} analytics={analytics} />
            );
          })}
        </div>
      )}

      {shouldRenderChart && (
        <BarChart width={width} height={height} data={pages}>
          <Bar
            ref={(element: any) => {
              barChartRef.current = element;
            }}
            dataKey="avgTimeSpent"
            xAxisId="index"
            yAxisId="avgTimeSpent"
            fill={"#dbdbdb"}
            maxBarSize={30}
            minPointSize={10}
            shape={(props: any) => {
              var stroke = 2;
              return (
                <g fill="none">
                  <rect
                    x={props.x}
                    y={props.y}
                    width={props.width}
                    height={props.height}
                    fill={props.fill}
                  />
                </g>
              );
            }}
          ></Bar>

          <Tooltip
            cursor={{ fill: separatorColor }}
            content={(props) => {
              return renderTooltip(props);
            }}
          />
        </BarChart>
      )}
    </div>
  );
};

export default PageAnalytics;

interface PageData {
  page: {
    index: number;
    avgTimeSpent: number;
    background: {
      full: string;
      thumbnail: string;
    };
    height: number;
    width: number;
  };
  analytics: {
    dataPoints: {
      index: number;
      avgTimeSpent: number;
    }[];
  };
}

const PageData = ({ page, analytics }: PageData) => {
  const padNumber = (number = 0, pad: any) => {
    number = Math.ceil(number);

    const length = pad - String(number).length + 1;
    let padding = "";
    if (length > 0) {
      padding = Array(pad - String(number).length + 1).join("0");
    }
    return padding + number;
  };

  var image = page.background.full;
  var width = 100;
  var height = 100;

  height = (page.height / page.width) * width;
  height = Math.min(height, 100);

  var analyticsData = analytics.dataPoints.find((dataPoint: any) => {
    return dataPoint.index == page.index + 1;
  });

  var timeSpent = 0;
  if (analyticsData) {
    timeSpent = analyticsData.avgTimeSpent || 0;
  }

  let minutes = padNumber(Math.floor(timeSpent / 60), 2);
  let seconds = padNumber(timeSpent % 60, 2);
  let pageNumber = padNumber(page.index + 1, 2);

  return (
    <div className={styles.analytics} key={page.index}>
      <div className={styles.inner}>
        <img className={styles.previewIcon} src={image} />
        <div className={styles.metadata}>
          <div className={styles.page}>
            #: <span className={styles.numbers}>{pageNumber}</span>
          </div>
          <div className={styles.timeSpent}>
            ~ Time:{" "}
            <span className={styles.numbers}>{`${minutes}:${seconds}`}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
